import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineSearch, AiOutlineClose } from "react-icons/ai";
import { formatInstalledSince } from "../../Home/utils/homeUtils";
import { isAbortError } from "../../Home/hooks/hotelsCache";

const GreenMilesHotelsTable = () => {
  const navigate = useNavigate();
  const [hotelsStats, setHotelsStats] = useState([]);
  // Deux étages : la liste pilote l'affichage des lignes, les stats remplissent les
  // colonnes coûteuses ensuite sans jamais bloquer le rendu.
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Fonction pour calculer les 3 derniers mois
  const getLastThreeMonths = () => {
    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()); // 3 mois en arrière, même jour

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      start: formatDate(startDate),
      end: formatDate(endDate),
    };
  };

  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Charger le tag sélectionné depuis localStorage au montage
  const [selectedTag, setSelectedTag] = useState(() => {
    try {
      const savedTag = localStorage.getItem("greenMilesHotelsTable_selectedTag");
      return savedTag || "";
    } catch (e) {
      return "";
    }
  });

  const TAG_OPTIONS = [
    { value: "", label: "Tous les tags" },
    { value: "Lab", label: "Lab" },
    { value: "Live", label: "Live" },
    { value: "Fini", label: "Fini" },
    { value: "Waiting", label: "Waiting" },
    { value: "Démo", label: "Démo" },
    { value: "Autres", label: "Autres" },
  ];

  // Fonction pour mettre à jour le tag et le sauvegarder dans localStorage
  const handleTagChange = (newTag) => {
    setSelectedTag(newTag);
    try {
      localStorage.setItem("greenMilesHotelsTable_selectedTag", newTag);
    } catch (e) {
      console.warn("Impossible de sauvegarder le tag dans localStorage:", e);
    }
  };

  // `/hotels-stats` met plusieurs secondes. Sans annulation, quitter l'onglet laissait la
  // requête courir : sa réponse arrivait en plein rendu d'une autre page et occupait le
  // thread principal pour rien.
  const abortRef = useRef(null);

  const startRequest = () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    return abortRef.current.signal;
  };

  useEffect(() => {
    // Initialiser avec les 3 derniers mois
    const { start, end } = getLastThreeMonths();
    setStartInput(start);
    setEndInput(end);
    fetchHotelsList(start, end);

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const buildStatsUrl = (path, startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    const apiUrl = process.env.REACT_APP_API_URL || "/api";
    return params.toString() ? `${apiUrl}${path}?${params.toString()}` : `${apiUrl}${path}`;
  };

  const toRows = (hotelsData, extra) => [
    ...(hotelsData.equippedHotels || []).map((hotel) => ({ ...hotel, is_equipped: 1, ...extra })),
    ...(hotelsData.nonEquippedHotels || []).map((hotel) => ({ ...hotel, is_equipped: 0, ...extra })),
  ];

  /**
   * Étage 1 — la liste. Rend les lignes immédiatement (nom, tag, chambres QR, maintenance),
   * puis enchaîne sur les statistiques sans les attendre.
   */
  const fetchHotelsList = async (startDate = null, endDate = null) => {
    const signal = startRequest();

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(buildStatsUrl("/hotels-stats/list", startDate, endDate), { signal });
      if (!response.ok) {
        throw new Error("Erreur lors de la récupération de la liste des hôtels");
      }
      const data = await response.json();
      if (data.success !== "OK") {
        throw new Error(data.message || "Erreur lors de la récupération des données");
      }

      setHotelsStats(toRows(data.data || {}, { statsLoaded: false }));
    } catch (err) {
      // Annulation volontaire (démontage, nouveau filtre) : ce n'est pas une erreur.
      if (isAbortError(err)) return;
      console.error("Error fetching hotels list:", err);
      setError(err.message);
    } finally {
      if (!signal.aborted) setLoading(false);
    }

    if (signal.aborted) return;
    fetchHotelsStats(startDate, endDate, signal);
  };

  /**
   * Étage 2 — les statistiques, fusionnées par hId dans les lignes déjà affichées.
   * Volontairement non awaité par l'appelant : les colonnes se remplissent quand elles
   * arrivent, sans jamais retarder le tableau.
   */
  const fetchHotelsStats = async (startDate = null, endDate = null, signal = undefined) => {
    try {
      setStatsLoading(true);
      setStatsError(null);

      const response = await fetch(buildStatsUrl("/hotels-stats", startDate, endDate), { signal });
      if (!response.ok) {
        throw new Error("Erreur lors de la récupération des statistiques");
      }
      const data = await response.json();
      if (data.success !== "OK") {
        throw new Error(data.message || "Erreur lors de la récupération des données");
      }

      const statsById = new Map(toRows(data.data || {}, {}).map((hotel) => [hotel.hId, hotel]));
      setHotelsStats((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          ...(statsById.get(row.hId) || {}),
          // Le drapeau passe à true sur TOUTES les lignes, même absentes de la réponse :
          // une ligne sans stats doit afficher ses valeurs à zéro, pas rester sur "...".
          statsLoaded: true,
        }))
      );
    } catch (err) {
      // Annulation volontaire : on ne touche ni au drapeau ni à l'erreur, le composant part.
      if (isAbortError(err)) return;
      console.error("Error fetching hotel stats:", err);
      // On lève quand même le drapeau, sinon les cellules restent bloquées sur "...".
      setHotelsStats((prevRows) => prevRows.map((row) => ({ ...row, statsLoaded: true })));
      setStatsError("Les statistiques n'ont pas pu être chargées");
    } finally {
      if (!signal?.aborted) setStatsLoading(false);
    }
  };

  const applyFilter = () => {
    fetchHotelsList(startInput || null, endInput || null);
  };

  const handleHotelClick = (hotelId) => {
    try {
      const storageKey = `gm:statsDates:${hotelId}`;
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn("Erreur lors de la suppression des dates:", e);
    }
    // `from` pilote le bouton retour : on revient au parc GreenMiles, pas à /hotels.
    navigate(`/hotels/${hotelId}/greenmiles`, { state: { from: "/greenmiles" } });
  };

  const formatNumber = (value, digits = 0) => {
    const num = Number(value);
    if (!Number.isFinite(num)) {
      return "0";
    }
    return num.toLocaleString("fr-FR", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  };

  const formatKpiEuroPerRoomPerMonth = (value) =>
    value == null ? "—" : `${formatNumber(value, 2)} €`;

  const formatKpiPercentage = (value) =>
    value == null ? "—" : `${formatNumber(value, 1)}%`;

  const formatPercentage = (value, total) => {
    if (!total || total === 0) {
      return "0%";
    }
    if (!value || value === 0) {
      return "0%";
    }
    const percentage = ((value / total) * 100).toFixed(1);
    return `${percentage}%`;
  };

  const formatGreenMilesPercentage = (greenMilesStays, totalRealStays) => {
    // Logique spéciale pour les séjours GreenMiles comme dans generalStats.model.js
    if (totalRealStays && totalRealStays > 0) {
      // Calcul normal
      const percentage = ((greenMilesStays / totalRealStays) * 100).toFixed(1);
      return `${percentage}% (${formatNumber(greenMilesStays)} / ${formatNumber(totalRealStays)})`;
    } else if (greenMilesStays && greenMilesStays > 0) {
      // Si totalRealStays = 0 mais qu'il y a des séjours GreenMiles → 100%
      return `100.0% (${formatNumber(greenMilesStays)} / ${formatNumber(greenMilesStays)})`;
    } else {
      // Aucun séjour
      return `0% (0 / 0)`;
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Fonction pour obtenir l'ordre de tri des tags
  const getTagSortOrder = (tag) => {
    const tagOrder = {
      "Lab": 1,
      "Live": 2,
      "Fini": 3,
      "Waiting": 4,
      "Démo": 5,
      "Autres": 6,
    };
    // Si le tag n'est pas dans la liste, le mettre à la fin (ordre 99)
    return tagOrder[tag] || 99;
  };

  const getSortableValue = (hotel, key) => {
    switch (key) {
      case "hName":
        return hotel.hName || "";
      case "hTags":
        // Pour le tri des tags, retourner l'ordre numérique
        return getTagSortOrder(hotel.hTags || "");
      case "roomsWithQrCode":
        return hotel.roomsWithQrCode || 0;
      case "sejoursGreenMiles":
        // Pour le tri, calculer le pourcentage réel
        if (hotel.totalRealStays && hotel.totalRealStays > 0) {
          return (hotel.sejoursGreenMiles / hotel.totalRealStays) * 100;
        } else if (hotel.sejoursGreenMiles && hotel.sejoursGreenMiles > 0) {
          return 100.0; // Cas spécial : 100% quand totalRealStays = 0
        }
        return 0;
      case "sejoursGMWithActions":
        // Pour le tri, calculer le pourcentage réel
        if (hotel.sejoursGreenMiles && hotel.sejoursGreenMiles > 0) {
          return (hotel.sejoursGMWithActions / hotel.sejoursGreenMiles) * 100;
        }
        return 0;
      case "sejoursGMWithRewardsUsed":
        // Pour le tri, calculer le pourcentage réel
        if (hotel.sejoursGreenMiles && hotel.sejoursGreenMiles > 0) {
          return (hotel.sejoursGMWithRewardsUsed / hotel.sejoursGreenMiles) * 100;
        }
        return 0;
      case "maintenanceMode":
        // ON en premier, OFF ensuite, valeur absente en dernier
        if (hotel.isMaintenance === true) return 0;
        if (hotel.isMaintenance === false) return 1;
        return 2;
      case "avgEuroSavingsPerRoomPerMonth":
        return hotel.avgEuroSavingsPerRoomPerMonth ?? -1;
      case "staysWithEffectiveActionsPercentage":
        return hotel.staysWithEffectiveActionsPercentage ?? -1;
      default:
        return 0;
    }
  };

  const sortHotels = (hotels) => {
    if (!sortConfig.key) return hotels;

    return [...hotels].sort((a, b) => {
      const aValue = getSortableValue(a, sortConfig.key);
      const bValue = getSortableValue(b, sortConfig.key);

      // Tri spécial pour les noms d'hôtels (texte)
      if (sortConfig.key === "hName") {
        const aStr = (aValue || "").toString().toLowerCase();
        const bStr = (bValue || "").toString().toLowerCase();
        if (aStr < bStr) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aStr > bStr) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      }

      // Conversion en nombres pour les valeurs numériques (y compris les tags qui retournent un ordre numérique)
      const aNum = typeof aValue === "number" ? aValue : parseFloat(aValue) || 0;
      const bNum = typeof bValue === "number" ? bValue : parseFloat(bValue) || 0;

      if (aNum < bNum) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aNum > bNum) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const SortableHeader = ({ sortKey, children, className = "" }) => (
    <th className={`px-3 py-3 text-center text-xs font-medium text-black tracking-wider cursor-pointer hover:bg-gray-100 transition-colors ${className}`} onClick={() => handleSort(sortKey)}>
      <div className="flex items-center justify-center space-x-1">
        <span>{children}</span>
        <span className="text-gray-400">{sortConfig.key === sortKey ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕"}</span>
      </div>
    </th>
  );

  const renderHotelLanguageFlags = (hotel) => {
    if (!Array.isArray(hotel.languages) || hotel.languages.length === 0) return null;
    return (
      <span className="inline-flex items-center gap-1 ml-2 align-middle">
        {hotel.languages.map((lang) => {
          const title = `${lang.name || lang.code}${lang.isPrimary ? " (principale)" : ""}`;
          return lang.flagURL ? (
            <img key={lang.code} src={lang.flagURL} alt={lang.code} title={title} className="w-4 h-4 rounded-full object-cover inline-block" />
          ) : (
            <span key={lang.code} title={title} className="text-[10px] font-semibold uppercase text-gray-500 border border-gray-300 rounded px-0.5 leading-tight">
              {lang.code}
            </span>
          );
        })}
      </span>
    );
  };

  const renderHotelNameCell = (hotel) => {
    const installedSince = formatInstalledSince(hotel.installationDate);
    return (
      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-black">
        <div>
          {hotel.hName}
          {renderHotelLanguageFlags(hotel)}
        </div>
        {installedSince && <div className="mt-1 text-xs font-normal text-gray-500">{installedSince}</div>}
      </td>
    );
  };

  if (loading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-8 text-center">
        <div className="text-black text-lg">Chargement des hôtels...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow-md rounded-lg p-8 text-center">
        <div className="text-red-500 text-lg">Erreur: {error}</div>
      </div>
    );
  }

  if (hotelsStats.length === 0) {
    return (
      <div className="bg-white shadow-md rounded-lg p-8 text-center">
        <div className="text-black text-lg">Aucun hôtel GreenMiles disponible pour le moment.</div>
      </div>
    );
  }

  // Fonction pour filtrer les hôtels par terme de recherche
  const filterHotelsBySearch = (hotels) => {
    if (!searchTerm.trim()) return hotels;
    const term = searchTerm.toLowerCase().trim();
    return hotels.filter((hotel) => hotel.hName && hotel.hName.toLowerCase().includes(term));
  };

  // Fonction pour filtrer les hôtels par tag
  const filterHotelsByTag = (hotels) => {
    if (!selectedTag) return hotels;
    if (selectedTag === "Autres") {
      // Pour "Autres", on filtre les hôtels qui n'ont pas de tag ou un tag non standard
      const standardTags = ["Lab", "Live", "Fini", "Waiting", "Démo"];
      return hotels.filter((hotel) => !hotel.hTags || !standardTags.includes(hotel.hTags));
    }
    return hotels.filter((hotel) => hotel.hTags === selectedTag);
  };

  // Fonction combinée pour filtrer par recherche et tag
  const applyFilters = (hotels) => {
    let filtered = hotels;
    filtered = filterHotelsByTag(filtered);
    filtered = filterHotelsBySearch(filtered);
    return filtered;
  };

  // Séparer les hôtels équipés et non-équipés, filtrer par recherche et tag, puis les trier
  const equippedHotels = Array.isArray(hotelsStats) ? sortHotels(applyFilters(hotelsStats.filter((hotel) => hotel.is_equipped === 1))) : [];
  const nonEquippedHotels = Array.isArray(hotelsStats) ? sortHotels(applyFilters(hotelsStats.filter((hotel) => hotel.is_equipped === 0))) : [];

  return (
    <div className="space-y-6">
      {/* Header avec titre et période d'analyse */}
      <div className="mb-6 flex justify-between items-start">
        <h1 className="text-3xl font-bold text-gray-900">Parc GreenMiles</h1>
        <div className="flex flex-col items-start">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Période d'analyse</h2>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Filtrer les données de l'hôtel de</label>
            <input type="date" value={startInput} onChange={(e) => setStartInput(e.target.value)} className="px-3 py-1 border border-gray-300 rounded text-sm" />
            <span className="text-sm text-gray-600">à</span>
            <input type="date" value={endInput} onChange={(e) => setEndInput(e.target.value)} className="px-3 py-1 border border-gray-300 rounded text-sm" />
            <button onClick={applyFilter} className="px-4 py-2 text-white rounded-full text-sm font-semibold" style={{ backgroundColor: "#3B5543" }}>
              Valider
            </button>
          </div>
        </div>
      </div>

      {/* Barre de recherche - toujours visible */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-black">Hôtel GreenMiles équipé</h3>
          {statsLoading && <span className="text-sm text-gray-500">Chargement des statistiques...</span>}
          {!statsLoading && statsError && <span className="text-sm text-red-600">{statsError}</span>}
          <div className="relative" style={{ minWidth: "250px" }}>
            <AiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher un hôtel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Effacer la recherche"
              >
                <AiOutlineClose size={18} />
              </button>
            )}
          </div>
          <div className="relative">
            <select
              value={selectedTag}
              onChange={(e) => handleTagChange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
              style={{ minWidth: "150px" }}
            >
              {TAG_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button onClick={() => navigate("/greenmiles/create-hotel")} className="px-4 py-2 bg-gray-200 text-black rounded-md hover:bg-gray-300 transition-colors">
          Créer un hotel
        </button>
      </div>

      {/* Message si aucun résultat de recherche */}
      {searchTerm.trim() && equippedHotels.length === 0 && nonEquippedHotels.length === 0 && (
        <div className="bg-white shadow-md rounded-lg p-8 text-center">
          <div className="text-black text-lg">Aucun hôtel trouvé pour "{searchTerm}"</div>
        </div>
      )}

      {/* Hôtels équipés */}
      {equippedHotels.length > 0 && (
        <div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader sortKey="hName">Nom de l'hotel</SortableHeader>
                    <SortableHeader sortKey="hTags">Tags</SortableHeader>
                    <SortableHeader sortKey="roomsWithQrCode">
                      Chambres
                      <br />
                      avec QR codes
                    </SortableHeader>
                    <SortableHeader sortKey="sejoursGreenMiles">Séjours GreenMiles</SortableHeader>
                    <SortableHeader sortKey="sejoursGMWithActions">
                      Séjours GM avec
                      <br />
                      au moins 1 action
                    </SortableHeader>
                    <SortableHeader sortKey="sejoursGMWithRewardsUsed">
                      Séjours GM avec
                      <br />
                      récompense utilisée
                    </SortableHeader>
                    <SortableHeader sortKey="avgEuroSavingsPerRoomPerMonth" className="border-l border-gray-200">
                      Économie moyenne
                      <br />
                      €/chambre/mois
                    </SortableHeader>
                    <SortableHeader sortKey="staysWithEffectiveActionsPercentage">
                      Séjours avec
                      <br />
                      action efficace (%)
                    </SortableHeader>
                    <SortableHeader sortKey="maintenanceMode">Maintenance</SortableHeader>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {equippedHotels.map((hotel, index) => (
                    <tr key={hotel.hId} className="bg-white hover:bg-gray-100 cursor-pointer" onClick={() => handleHotelClick(hotel.hId)}>
                      {renderHotelNameCell(hotel)}
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-black">{hotel.hTags || "-"}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-black">{formatNumber(hotel.roomsWithQrCode)}</td>
                      {/* Colonnes alimentées par /hotels-stats : placeholder tant que le lot n'est pas revenu. */}
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-black">{!hotel.statsLoaded ? <span className="text-gray-400">...</span> : formatGreenMilesPercentage(hotel.sejoursGreenMiles, hotel.totalRealStays)}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-black">
                        {!hotel.statsLoaded ? (
                          <span className="text-gray-400">...</span>
                        ) : (
                          <>
                            {formatPercentage(hotel.sejoursGMWithActions, hotel.sejoursGreenMiles)} ({formatNumber(hotel.sejoursGMWithActions)} / {formatNumber(hotel.sejoursGreenMiles)})
                          </>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-black">
                        {!hotel.statsLoaded ? (
                          <span className="text-gray-400">...</span>
                        ) : (
                          <>
                            {formatPercentage(hotel.sejoursGMWithRewardsUsed, hotel.sejoursGreenMiles)} ({formatNumber(hotel.sejoursGMWithRewardsUsed)} / {formatNumber(hotel.sejoursGreenMiles)})
                          </>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-black border-l border-gray-200">
                        {!hotel.statsLoaded ? <span className="text-gray-400">...</span> : hotel.hasEffectiveActions ? formatKpiEuroPerRoomPerMonth(hotel.avgEuroSavingsPerRoomPerMonth) : "—"}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-black">
                        {!hotel.statsLoaded ? (
                          <span className="text-gray-400">...</span>
                        ) : hotel.hasEffectiveActions ? (
                          <>
                            {formatKpiPercentage(hotel.staysWithEffectiveActionsPercentage)}
                            <span className="text-black ml-1 text-xs">
                              ({formatNumber(hotel.sejoursGMWithEffectiveActions)} / {formatNumber(hotel.sejoursGreenMiles)})
                            </span>
                          </>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-semibold">
                        {hotel.isMaintenance === true && <span className="text-green-700">ON</span>}
                        {hotel.isMaintenance === false && <span className="text-red-600">OFF</span>}
                        {hotel.isMaintenance == null && <span className="text-red-600">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Hôtels non-équipés */}
      {nonEquippedHotels.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-black mb-4">Hôtel GreenMiles non-équipé</h3>
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-black tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort("hName")}>
                      <div className="flex items-center space-x-1">
                        <span>Nom de l'hotel</span>
                        <span className="text-gray-400">{sortConfig.key === "hName" ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕"}</span>
                      </div>
                    </th>
                    <SortableHeader sortKey="hTags">Tags</SortableHeader>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {nonEquippedHotels.map((hotel, index) => (
                    <tr key={hotel.hId} className="bg-white hover:bg-gray-100 cursor-pointer" onClick={() => handleHotelClick(hotel.hId)}>
                      {renderHotelNameCell(hotel)}
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-black">{hotel.hTags || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GreenMilesHotelsTable;
